import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Define public routes that don't need authentication
const publicRoutes = [
  '/company-user-login',
  '/public-vendor-registration-form',
  '/public-vendor-acknowledgement'
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const refreshTimeoutRef = useRef(null);

  /**
   * Verify session by calling /auth/me
   * Cookies are sent automatically with withCredentials: true
   */
  const verifySession = useCallback(async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        // Schedule token refresh (14 minutes before expiry for 15-min tokens)
        scheduleTokenRefresh(14 * 60 * 1000);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session verification failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  }, []);

  /**
   * Schedule automatic token refresh
   */
  const scheduleTokenRefresh = useCallback((delay) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    refreshTimeoutRef.current = setTimeout(async () => {
      try {
        await authService.refresh();
        // Reschedule for next refresh
        scheduleTokenRefresh(14 * 60 * 1000);
      } catch (error) {
        console.error('Token refresh failed:', error);
        // If refresh fails, session is invalid
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/company-user-login';
      }
    }, delay);
  }, []);

  /**
   * Initialize auth state on app load
   */
  useEffect(() => {
    const initAuth = async () => {
      // Skip auth check for public routes
      const currentPath = window.location.pathname;
      if (publicRoutes.some(route => currentPath.startsWith(route))) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      await verifySession();
      setLoading(false);
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [verifySession]);

  /**
   * Login with email and password
   */
  const login = async (email, password) => {
    try {
      const response = await authService.login({ email, password });
      
      if (response.success && response.data?.user) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        // Schedule token refresh
        scheduleTokenRefresh(14 * 60 * 1000);
        return { success: true };
      }
      
      return { 
        success: false, 
        error: 'Login failed: Invalid response from server' 
      };
    } catch (error) {
      console.error('Login failed:', error);
      
      // Handle rate limit errors specifically
      if (error.response?.status === 429) {
        const resetTime = error.response?.headers?.['x-ratelimit-reset'];
        const remaining = error.response?.headers?.['x-ratelimit-remaining'];
        
        let message = 'Too many login attempts. Please wait a few minutes before trying again.';
        
        if (resetTime) {
          const resetDate = new Date(parseInt(resetTime) * 1000);
          const minutes = Math.ceil((resetDate.getTime() - Date.now()) / 60000);
          if (minutes > 0) {
            message = `Too many login attempts. Please try again in ${minutes} minute(s).`;
          }
        }
        
        return { success: false, error: message };
      }
      
      const message = error.response?.data?.error?.message 
        || error.response?.data?.detail 
        || error.message 
        || 'Login failed. Please check your credentials and try again.';
      return { success: false, error: message };
    }
  };

  /**
   * Logout - clear session and cookies
   */
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn('Logout error:', error);
    } finally {
      // Clear local state
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear any scheduled refresh
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Redirect to login
      window.location.href = '/company-user-login';
    }
  };

  /**
   * Logout from all devices
   */
  const logoutAll = async () => {
    try {
      await authService.logoutAll();
    } catch (error) {
      console.warn('Logout all error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      window.location.href = '/company-user-login';
    }
  };

  /**
   * Refresh user data from server
   */
  const refreshUser = async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (...roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    logoutAll,
    refreshUser,
    hasRole,
    hasAnyRole,
    // Computed role checks for convenience (case-insensitive)
    isAdmin: user?.role?.toUpperCase() === 'ADMIN',
    isCoordinator: user?.role?.toUpperCase() === 'COORDINATOR',
    isUser: user?.role?.toUpperCase() === 'USER',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
